import * as React from "react";
import {Col, ControlLabel, FormControl, FormGroup, HelpBlock, OverlayTrigger, Tooltip} from "react-bootstrap";
import {defineMessages, FormattedMessage, InjectedIntlProps, injectIntl} from "react-intl";
import {OGCConnectionConfig, OgcServiceType, EMPTY_OGC_CONNECTION, OGC_FILE_EXTENSION} from "./model";
import * as yup from "yup";
import {Formik, FormikProps} from "formik";

type OgcConnectionFormOnChange =
    ((event: { target: { name: "ogcConnection"; value: OGCConnectionConfig; } }) => void) &
    ((event: { target: { name: "isOgcConnectionValid"; value: boolean; } }) => void);

interface OgcConnectionFormOwnProps {
  onRef: (ref: OgcConnectionFormComponent) => void;
  onChange: OgcConnectionFormOnChange;
}

type OgcConnectionFormProps = OgcConnectionFormOwnProps & InjectedIntlProps;

interface OgcConnectionFormState {
  ogcConnection: OGCConnectionConfig;
}

const MESSAGES = defineMessages({
  ogcWMS: {
    id: "studio.add-data-dialog.ogc-wms",
    defaultMessage: "WMS",
  },
  ogcWFS: {
    id: "studio.add-data-dialog.ogc-wfs",
    defaultMessage: "WFS",
  },
  ogcWCS: {
    id: "studio.add-data-dialog.ogc-wcs",
    defaultMessage: "WCS",
  },
  ogcWMTS: {
    id: "studio.add-data-dialog.ogc-wmts",
    defaultMessage: "WMTS",
  },
  connectionNameError: {
    id: "studio.add-data-dialog.ogc-connection-name-error",
    defaultMessage: "Connection name is required",
  },
  urlError: {
    id: "studio.add-data-dialog.ogc-url-error",
    defaultMessage: "Service URL is required",
  },
  serviceTypeError: {
    id: "studio.add-data-dialog.ogc-service-type-error",
    defaultMessage: "Service Type is required",
  },
  layerError: {
    id: "studio.add-data-dialog.ogc-layer-error",
    defaultMessage: "Service Layer name is required",
  },
  CoverageError: {
    id: "studio.add-data-dialog.ogc-coverage-error",
    defaultMessage: "Service Coverage name is required",
  },
  FeatureTypeError: {
    id: "studio.add-data-dialog.ogc-feature-type-error",
    defaultMessage: "Service Feature type is required",
  },
  HELP: {
    id: "studio.UI.help-alt",
    defaultMessage: "❔",
  },

});

const LABEL_COL = 3;
const CONTROL_COL = 8;
const HELP_COL = 1;

export class OgcConnectionFormComponent extends React.Component<OgcConnectionFormProps, OgcConnectionFormState> {
  private validationSchema: any = this.createValidationSchema();
  private formik: FormikProps<any>;

  constructor(props: OgcConnectionFormProps) {
    super(props);
    props.onRef(this);
    this.state = {
      ogcConnection: EMPTY_OGC_CONNECTION,
    };
  }

  private createValidationSchema() {
    const {intl} = this.props;

    return yup.object().shape({
      ogcConnectionName: yup.string().required(intl.formatMessage(MESSAGES.connectionNameError)),
      ogcUrl: yup.string().required(intl.formatMessage(MESSAGES.urlError)),
      ogcServiceType: yup.string().required(intl.formatMessage(MESSAGES.serviceTypeError)),
      ogcLayer: yup.string().when(['ogcServiceType'], {
        is: (ogcServiceType) => (ogcServiceType === OgcServiceType.WMS || ogcServiceType === OgcServiceType.WMTS),
        then: (schema) => schema.required(intl.formatMessage(MESSAGES.layerError)),
        otherwise: (schema) => schema.notRequired()
      }),
      ogcCoverage: yup.string().when(['ogcServiceType'], {
        is: (ogcServiceType) => (ogcServiceType === OgcServiceType.WCS),
        then: (schema) => schema.required(intl.formatMessage(MESSAGES.CoverageError)),
        otherwise: (schema) => schema.notRequired()
      }),
      ogcFeatureType: yup.string().when(['ogcServiceType'], {
        is: (ogcServiceType) => (ogcServiceType === OgcServiceType.WFS),
        then: (schema) => schema.required(intl.formatMessage(MESSAGES.FeatureTypeError)),
        otherwise: (schema) => schema.notRequired()
      }),
    });
  }

  componentDidMount() {
    this.validateForm();
  }

  componentDidUpdate(_prevProps, prevState) {
    if (this.state.ogcConnection !== prevState.ogcConnection) {
      this.validateForm();
    }
  }

  private validateForm() {
    const {onChange} = this.props;

    this.formik.validateForm()
        .then((errors) => {
          onChange({target: {name: "isOgcConnectionValid", value: Object.keys(errors).length === 0}});
        });
  }

  updateState = (updatedPart) => this.setState(Object.assign({}, this.state, updatedPart));

  render() {
    const {ogcConnection} = this.state;
    const {intl} = this.props;

    return <Formik innerRef={ref => this.formik = ref} validationSchema={this.validationSchema}
                   initialValues={EMPTY_OGC_CONNECTION} onSubmit={() => null}>
      {({
          touched,
          errors,
        }) => (
          <React.Fragment>
            <FormGroup controlId="ogcConnectionName"
                       className={touched.ogcConnectionName && errors.ogcConnectionName ? "has-error" : "no-error"}>
              <Col componentClass={ControlLabel} sm={LABEL_COL}>
                <FormattedMessage id="studio.add-data-dialog.ogc-connection-name" defaultMessage="Connection name"/>
              </Col>
              <Col sm={CONTROL_COL}>
                <FormControl name="ogcConnectionName" value={ogcConnection.ogcConnectionName}
                             onChange={this.onOgcConnectionChange}/>
                <HelpBlock>{touched.ogcConnectionName && errors.ogcConnectionName}</HelpBlock>
              </Col>
              <Col sm={HELP_COL} className={"ogcConnectionForm-Label"}>
                <OverlayTrigger placement="top" overlay={<Tooltip
                    id="tooltip_help">
                  <FormattedMessage id="studio.add-data.ogc-connection.connection-name.help"
                                    defaultMessage="{connectionName}.{extension} will be created and uploaded."
                                    values={{
                                      connectionName: this.state.ogcConnection.ogcConnectionName !== ""
                                                      ? this.state.ogcConnection.ogcConnectionName : "*",
                                      extension: OGC_FILE_EXTENSION[this.state.ogcConnection.ogcServiceType]
                                    }}
                  />
                </Tooltip>}>
                  <img src="theme/assets/images/help_16.png" id="Connection-name"
                       alt={intl.formatMessage(MESSAGES.HELP)}
                       onMouseOver={this.displayHelp}
                       onMouseLeave={this.displayHelp}/>
                </OverlayTrigger>
              </Col>
            </FormGroup>
            <FormGroup controlId="ogcServiceType"
                       className={touched.ogcServiceType && errors.ogcServiceType ? "has-error" : "no-error"}>
              <Col componentClass={ControlLabel} sm={LABEL_COL}>
                <FormattedMessage id="studio.add-data-dialog.ogc-service-type" defaultMessage="Service type"/>
              </Col>
              <Col sm={CONTROL_COL}>
                <FormControl name="ogcServiceType" value={ogcConnection.ogcServiceType}
                             onChange={this.onOgcConnectionChange}
                             componentClass="select">
                  <option value={OgcServiceType.WMTS}>
                    {intl.formatMessage(MESSAGES.ogcWMTS)}
                  </option>
                  <option value={OgcServiceType.WMS}>
                    {intl.formatMessage(MESSAGES.ogcWMS)}
                  </option>
                  <option value={OgcServiceType.WFS}>
                    {intl.formatMessage(MESSAGES.ogcWFS)}
                  </option>
                  <option value={OgcServiceType.WCS}>
                    {intl.formatMessage(MESSAGES.ogcWCS)}
                  </option>
                </FormControl>
                <HelpBlock>{touched.ogcServiceType && errors.ogcServiceType}</HelpBlock>
              </Col>
              <Col sm={HELP_COL} className={"ogcConnectionForm-Label"}>
                <OverlayTrigger placement="top" overlay={<Tooltip
                    id="tooltip_help">
                  <FormattedMessage id="studio.add-data.ogc-connection.service-type.help"
                                    defaultMessage="OGC Service type"/>
                </Tooltip>}>
                  <img src="theme/assets/images/help_16.png" id="service-type"
                       alt={intl.formatMessage(MESSAGES.HELP)}
                       onMouseOver={this.displayHelp}
                       onMouseLeave={this.displayHelp}/>
                </OverlayTrigger>
              </Col>
            </FormGroup>
            <FormGroup controlId="ogcUrl" className={touched.ogcUrl && errors.ogcUrl ? "has-error" : "no-error"}>
              <Col componentClass={ControlLabel} sm={LABEL_COL}>
                <FormattedMessage id="studio.add-data-dialog.ogc-url" defaultMessage="URL"/>
              </Col>
              <Col sm={CONTROL_COL}>
                <FormControl name="ogcUrl" value={ogcConnection.ogcUrl} onChange={this.onOgcConnectionChange}/>
                <HelpBlock>{touched.ogcUrl && errors.ogcUrl}</HelpBlock>
              </Col>
              <Col sm={HELP_COL} className={"ogcConnectionForm-Label"}>
                <OverlayTrigger placement="top" overlay={<Tooltip
                    id="tooltip_help">
                  <FormattedMessage id="studio.add-data.ogc-connection.url.help"
                                    defaultMessage="URL used to connect to the OGC service"/>
                </Tooltip>}>
                  <img src="theme/assets/images/help_16.png" id="ogc-url"
                       alt={intl.formatMessage(MESSAGES.HELP)}
                       onMouseOver={this.displayHelp}
                       onMouseLeave={this.displayHelp}/>
                </OverlayTrigger>
              </Col>
            </FormGroup>
            {(ogcConnection.ogcServiceType === OgcServiceType.WMS || ogcConnection.ogcServiceType ===
              OgcServiceType.WMTS) &&
             <FormGroup controlId="ogcLayer" className={touched.ogcLayer && errors.ogcLayer ? "has-error" : "no-error"}>
               <Col componentClass={ControlLabel} sm={LABEL_COL}>
                 <FormattedMessage id="studio.add-data-dialog.ogc-layer-label" defaultMessage="Layer"/>
               </Col>
               <Col sm={CONTROL_COL}>
                 <FormControl name="ogcLayer" value={ogcConnection.ogcLayer} onChange={this.onOgcConnectionChange}/>
                 <HelpBlock>{touched.ogcLayer && errors.ogcLayer}</HelpBlock>
               </Col>
               <Col sm={HELP_COL} className={"ogcConnectionForm-Label"}>
                 <OverlayTrigger placement="top" overlay={<Tooltip
                     id="tooltip_help">
                   <FormattedMessage id="studio.add-data.ogc-connection.layer.help"
                                     defaultMessage="Name of the Layer"/>
                 </Tooltip>}>
                   <img src="theme/assets/images/help_16.png" id="ogc-layer"
                        alt={intl.formatMessage(MESSAGES.HELP)}
                        onMouseOver={this.displayHelp}
                        onMouseLeave={this.displayHelp}/>
                 </OverlayTrigger>
               </Col>
             </FormGroup>
            }
            {(ogcConnection.ogcServiceType === OgcServiceType.WCS) &&
             <FormGroup controlId="ogcCoverage"
                        className={touched.ogcCoverage && errors.ogcCoverage ? "has-error" : "no-error"}>
               <Col componentClass={ControlLabel} sm={LABEL_COL}>
                 <FormattedMessage id="studio.add-data-dialog.ogc-coverage-label" defaultMessage="Coverage"/>
               </Col>
               <Col sm={CONTROL_COL}>
                 <FormControl name="ogcCoverage" value={ogcConnection.ogcCoverage}
                              onChange={this.onOgcConnectionChange}/>
                 <HelpBlock>{touched.ogcCoverage && errors.ogcCoverage}</HelpBlock>
               </Col>
               <Col sm={HELP_COL} className={"ogcConnectionForm-Label"}>
                 <OverlayTrigger placement="top" overlay={<Tooltip
                     id="tooltip_help">
                   <FormattedMessage id="studio.add-data.ogc-connection.coverage.help"
                                     defaultMessage="Name of the Coverage"/>
                 </Tooltip>}>
                   <img src="theme/assets/images/help_16.png" id="ogc-coverage"
                        alt={intl.formatMessage(MESSAGES.HELP)}
                        onMouseOver={this.displayHelp}
                        onMouseLeave={this.displayHelp}/>
                 </OverlayTrigger>
               </Col>
             </FormGroup>
            }
            {(ogcConnection.ogcServiceType === OgcServiceType.WFS) &&
             <FormGroup controlId="ogcFeatureType"
                        className={touched.ogcFeatureType && errors.ogcFeatureType ? "has-error" : "no-error"}>
               <Col componentClass={ControlLabel} sm={LABEL_COL}>
                 <FormattedMessage id="studio.add-data-dialog.ogc-feature-type-label" defaultMessage="Feature type"/>
               </Col>
               <Col sm={CONTROL_COL}>
                 <FormControl name="ogcFeatureType" value={ogcConnection.ogcFeatureType}
                              onChange={this.onOgcConnectionChange}/>
                 <HelpBlock>{touched.ogcFeatureType && errors.ogcFeatureType}</HelpBlock>
               </Col>
               <Col sm={HELP_COL} className={"ogcConnectionForm-Label"}>
                 <OverlayTrigger placement="top" overlay={<Tooltip
                     id="tooltip_help">
                   <FormattedMessage id="studio.add-data.ogc-connection.feature-type.help"
                                     defaultMessage="Name of the Feature type"/>
                 </Tooltip>}>
                   <img src="theme/assets/images/help_16.png" id="ogc-feature-type"
                        alt={intl.formatMessage(MESSAGES.HELP)}
                        onMouseOver={this.displayHelp}
                        onMouseLeave={this.displayHelp}/>
                 </OverlayTrigger>
               </Col>
             </FormGroup>
            }
          </React.Fragment>)}
    </Formik>
  }

  private displayHelp = (event: any) => {
    if (event.type === "mouseover") {
      this.updateState({displayHelp: event.target.id});
    } else {
      this.updateState({displayHelp: ""});
    }
  }

  private onOgcConnectionChange = (event: any) => {
    const {onChange} = this.props;
    const {name, value} = event.target;
    const updatedOgcConnection = Object.assign({}, this.state.ogcConnection, {
      [name]: value
    });

    onChange({target: {name: "ogcConnection", value: updatedOgcConnection}});
    this.formik.setFieldTouched(name, true, false);
    this.formik.handleChange(event);
    this.updateState({ogcConnection: updatedOgcConnection});
  }

  public showAllErrors() { //Used in AddDataDialog.tsx
    this.formik.submitForm();
  }
}

export const OgcConnectionForm = injectIntl(OgcConnectionFormComponent);